// Admin User 360 Page - Master consolidated user overview
import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AuthContext } from "../../App";
import { 
  ArrowLeft, RefreshCw, User, Shield, Coins, Flag, AlertTriangle, 
  Calendar, Clock, ExternalLink, CheckCircle, XCircle, FileText, Gift
} from "lucide-react";
import { ADMIN_ROUTES } from "../../config/adminRoutes";

const API = window.location.origin;

function Pill({ children, tone = "slate" }) {
  const map = {
    slate: "border-slate-700 bg-slate-800 text-slate-300",
    rose: "border-rose-500/30 bg-rose-500/10 text-rose-400",
    emerald: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
    indigo: "border-indigo-500/30 bg-indigo-500/10 text-indigo-400",
    amber: "border-amber-500/30 bg-amber-500/10 text-amber-400",
    violet: "border-violet-500/30 bg-violet-500/10 text-violet-400",
  };
  return (
    <span className={`text-xs px-3 py-1 rounded-full border font-medium ${map[tone] || map.slate}`}>
      {children}
    </span>
  );
}

function fmtDate(v) {
  try {
    return v ? new Date(v).toLocaleString() : "—";
  } catch {
    return "—";
  }
}

function StatCard({ icon: Icon, label, value, subvalue, tone = "slate" }) {
  const iconColors = {
    slate: "text-slate-400",
    rose: "text-rose-400",
    emerald: "text-emerald-400",
    amber: "text-amber-400",
    indigo: "text-indigo-400",
    violet: "text-violet-400",
  };
  return (
    <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-4">
      <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
        <Icon className={`w-4 h-4 ${iconColors[tone]}`} />
        {label}
      </div>
      <div className="text-lg font-semibold text-slate-200">{value}</div>
      {subvalue && <div className="text-xs text-slate-500 mt-1">{subvalue}</div>}
    </div>
  );
}

export default function AdminUser360Page() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { token, user: adminUser } = useContext(AuthContext);

  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const isAdmin = adminUser?.is_admin;

  async function load() {
    if (!isAdmin || !userId) return;
    setBusy(true);
    setToast(null);
    try {
      const res = await fetch(`${API}/api/admin/users/${userId}/overview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.detail || "Failed to load user overview");
      setData(json);
    } catch (e) {
      setToast({ type: "error", msg: e.message });
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    load();
    
  }, [userId, isAdmin]);

  // Check admin access
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-slate-400">Admin access required</div>
      </div>
    );
  }

  const u = data?.user || {};
  const s = data?.summary || {};
  const tier = u.subscription_tier || "free";
  const riskHold = !!u.risk_hold;

  const tierTone = tier === "elite_plus" ? "indigo" : tier === "elite" ? "emerald" : "slate";

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-6">
          <div>
            <button
              onClick={() => navigate("/admin")}
              className="inline-flex items-center space-x-2 text-sm text-slate-400 hover:text-slate-200 transition mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Admin</span>
            </button>
            <h1 className="text-2xl font-bold tracking-tight bg-gradient-to-r from-violet-400 to-indigo-500 bg-clip-text text-transparent">
              User 360°
            </h1>
          </div>
          <button
            onClick={load}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-800 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-slate-700 disabled:opacity-50 transition"
          >
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
            {busy ? "Loading…" : "Refresh"}
          </button>
        </div>

        {/* Toast */}
        {toast && (
          <div
            className={`mb-4 rounded-xl border px-4 py-3 text-sm flex items-center gap-2 ${
              toast.type === "error"
                ? "border-rose-500/30 bg-rose-500/10 text-rose-400"
                : "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
            }`}
          >
            {toast.type === "error" ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
            {toast.msg}
          </div>
        )}

        {data && (
          <>
            {/* Identity Card */}
            <div className="rounded-2xl border border-slate-800 bg-slate-900/50 overflow-hidden mb-6">
              <div className="p-4 border-b border-slate-800 bg-slate-800/30">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-xl bg-violet-500/10">
                      <User className="w-6 h-6 text-violet-400" />
                    </div>
                    <div>
                      <div className="text-xl font-semibold text-slate-100">
                        {u.email || "Unknown email"}
                      </div>
                      <div className="text-xs text-slate-500 font-mono mt-1">
                        ID: {u.id || userId}
                      </div>
                      {u.name && (
                        <div className="text-sm text-slate-400 mt-1">{u.name}</div>
                      )}
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Pill tone={tierTone}>{tier.toUpperCase()}</Pill>
                        {u.elite_active && <Pill tone="emerald">Active</Pill>}
                        {riskHold && <Pill tone="rose">⚠️ RISK HOLD</Pill>}
                        {u.shadowbanned && <Pill tone="rose">Shadowbanned</Pill>}
                        {s?.lounge?.active && <Pill tone="amber">🛋️ Lounge Pass</Pill>}
                        {u.is_admin && <Pill tone="violet">Admin</Pill>}
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => navigate(ADMIN_ROUTES.USER_ENTITLEMENTS(userId))}
                      className="inline-flex items-center gap-2 rounded-xl border border-purple-500/30 bg-purple-500/10 px-4 py-2 text-sm font-medium text-purple-400 hover:bg-purple-500/20 transition"
                    >
                      <Gift className="w-4 h-4" />
                      Entitlement Overrides
                    </button>
                    <button
                      onClick={() => navigate(`/admin/moderation?q=${encodeURIComponent(u.email || u.id || userId)}`)}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-800 px-4 py-2 text-sm font-medium text-slate-300 hover:bg-slate-700 transition"
                    >
                      <Shield className="w-4 h-4" />
                      Moderation
                    </button>
                    <button
                      onClick={() => navigate(`/admin/reports?q=${encodeURIComponent(u.email || u.id || userId)}`)}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-800 px-4 py-2 text-sm font-medium text-slate-300 hover:bg-slate-700 transition"
                    >
                      <Flag className="w-4 h-4" />
                      Reports
                    </button>
                    <button
                      onClick={() => navigate(`/admin/disputes?q=${encodeURIComponent(u.email || u.id || userId)}`)}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-800 px-4 py-2 text-sm font-medium text-slate-300 hover:bg-slate-700 transition"
                    >
                      <AlertTriangle className="w-4 h-4" />
                      Disputes
                    </button>
                  </div>
                </div>
              </div>

              {/* Risk Hold Banner */}
              {riskHold && (
                <div className="px-4 py-3 bg-rose-500/10 border-b border-rose-500/20">
                  <div className="flex items-center gap-3 text-rose-400">
                    <AlertTriangle className="w-5 h-5" />
                    <div>
                      <div className="font-semibold">Risk Hold Active</div>
                      <div className="text-sm text-rose-300/80">
                        Reason: {u.risk_hold_reason || "Unknown"} 
                        {u.risk_hold_dispute_id && ` • Dispute: ${u.risk_hold_dispute_id}`}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Stats Grid */}
              <div className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <StatCard
                    icon={Coins}
                    label="LoveCoins Balance"
                    value={u.lovecoins_balance ?? 0}
                    tone="amber"
                  />
                  <StatCard
                    icon={Flag}
                    label="Reports Against"
                    value={`${s?.reports?.open ?? 0} open`}
                    subvalue={`${s?.reports?.total ?? 0} total`}
                    tone={s?.reports?.open > 0 ? "rose" : "slate"}
                  />
                  <StatCard
                    icon={AlertTriangle}
                    label="Disputes"
                    value={`${s?.disputes?.open ?? 0} open`}
                    subvalue={`${s?.disputes?.total ?? 0} total`}
                    tone={s?.disputes?.open > 0 ? "rose" : "slate"}
                  />
                  <StatCard
                    icon={Clock}
                    label="Lounge Expires"
                    value={s?.lounge?.active ? "Active" : "Inactive"}
                    subvalue={fmtDate(s?.lounge?.expires_at)}
                    tone={s?.lounge?.active ? "amber" : "slate"}
                  />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                  <StatCard
                    icon={Calendar}
                    label="Created"
                    value={fmtDate(u.created_at)}
                    tone="slate"
                  />
                  <StatCard
                    icon={Calendar}
                    label="Plan Expires"
                    value={fmtDate(u.plan_expires_at)}
                    tone="slate"
                  />
                  <StatCard
                    icon={Calendar}
                    label="Plan Renews"
                    value={fmtDate(u.plan_renews_at)}
                    tone="slate"
                  />
                  <StatCard
                    icon={Coins}
                    label="Referral Earnings"
                    value={`$${((s?.referral_earnings?.total || 0) / 100).toFixed(2)}`}
                    subvalue={`Pending: $${((s?.referral_earnings?.pending || 0) / 100).toFixed(2)}`}
                    tone="violet"
                  />
                </div>
              </div>
            </div>

            {/* Activity Panels */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* LoveCoins Activity */}
              <div className="rounded-2xl border border-slate-800 bg-slate-900/50 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-800 bg-slate-800/30">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-200">
                    <Coins className="w-4 h-4 text-amber-400" />
                    LoveCoins Activity
                  </div>
                </div>
                <div className="p-4 max-h-80 overflow-y-auto space-y-2">
                  {(data?.lovecoin_transactions || []).slice(0, 15).map((t, idx) => (
                    <div key={idx} className="rounded-xl border border-slate-700 bg-slate-800/50 p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-medium text-slate-200">{t.type || "transaction"}</div>
                        <div className="text-xs text-slate-500">{fmtDate(t.created_at)}</div>
                      </div>
                      <div className={`text-sm mt-1 ${t.amount >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                        {t.amount >= 0 ? "+" : ""}{t.amount} coins
                      </div>
                    </div>
                  ))}
                  {(data?.lovecoin_transactions || []).length === 0 && (
                    <div className="text-sm text-slate-500 text-center py-4">No transactions</div>
                  )}
                </div>
              </div>

              {/* Admin Audit Log */}
              <div className="rounded-2xl border border-slate-800 bg-slate-900/50 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-800 bg-slate-800/30">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-200">
                    <FileText className="w-4 h-4 text-violet-400" />
                    Admin Actions
                  </div>
                </div>
                <div className="p-4 max-h-80 overflow-y-auto space-y-2">
                  {(data?.admin_audit || []).slice(0, 20).map((a, idx) => (
                    <div key={idx} className="rounded-xl border border-slate-700 bg-slate-800/50 p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-medium text-slate-200">{a.type}</div>
                        <div className="text-xs text-slate-500">{fmtDate(a.created_at)}</div>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {a.flag && `flag=${a.flag} → ${a.enabled ? "ON" : "OFF"}`}
                        {a.dispute_id && `dispute=${a.dispute_id}`}
                        {a.report_id && `report=${a.report_id}`}
                        {a.status && `status=${a.status}`}
                        {a.admin_email && ` by ${a.admin_email}`}
                      </div>
                    </div>
                  ))}
                  {(data?.admin_audit || []).length === 0 && (
                    <div className="text-sm text-slate-500 text-center py-4">No admin actions logged</div>
                  )}
                </div>
              </div>

              {/* Entitlement Ledger */}
              <div className="rounded-2xl border border-slate-800 bg-slate-900/50 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-800 bg-slate-800/30">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-200">
                    <Shield className="w-4 h-4 text-emerald-400" />
                    Entitlement Ledger
                  </div>
                </div>
                <div className="p-4 max-h-80 overflow-y-auto space-y-2">
                  {(data?.entitlement_ledger || []).slice(0, 15).map((e, idx) => (
                    <div key={idx} className="rounded-xl border border-slate-700 bg-slate-800/50 p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-medium text-slate-200">{e.ledger_type || e.type || "entry"}</div>
                        <div className="text-xs text-slate-500">{fmtDate(e.created_at)}</div>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {e.sku && `SKU: ${e.sku}`}
                        {e.amount_cents != null && ` • $${(e.amount_cents / 100).toFixed(2)}`}
                      </div>
                    </div>
                  ))}
                  {(data?.entitlement_ledger || []).length === 0 && (
                    <div className="text-sm text-slate-500 text-center py-4">No entitlement history</div>
                  )}
                </div>
              </div>

              {/* Moderation Logs */}
              <div className="rounded-2xl border border-slate-800 bg-slate-900/50 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-800 bg-slate-800/30">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-200">
                    <Flag className="w-4 h-4 text-rose-400" />
                    Moderation History
                  </div>
                </div>
                <div className="p-4 max-h-80 overflow-y-auto space-y-2">
                  {(data?.moderation_logs || []).slice(0, 15).map((m, idx) => (
                    <div key={idx} className="rounded-xl border border-slate-700 bg-slate-800/50 p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-medium text-slate-200">{m.action || m.type || "action"}</div>
                        <div className="text-xs text-slate-500">{fmtDate(m.created_at)}</div>
                      </div>
                      {m.reason && (
                        <div className="text-xs text-slate-500 mt-1">Reason: {m.reason}</div>
                      )}
                    </div>
                  ))}
                  {(data?.moderation_logs || []).length === 0 && (
                    <div className="text-sm text-slate-500 text-center py-4">No moderation history</div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {!data && !busy && (
          <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-8 text-center">
            <div className="text-slate-500">User not found</div>
          </div>
        )}
      </div>
    </div>
  );
}
